/**
 * Enhanced Reporting Page
 * Displays reports from all RCM Platform services
 * Provides collapsible sidebar navigation for service-specific reports
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  Download,
  Calendar,
  Filter,
  ChevronDown,
  ChevronRight,
  BarChart3,
  AlertTriangle,
  DollarSign,
  Shield,
  CheckCircle,
  CreditCard,
  Globe,
  RefreshCw,
  ExternalLink,
  LineChart,
  Wallet,
  ShieldAlert,
} from 'lucide-react';
import { SERVICE_PORTS } from '../../utils/constants';
import { buildServiceOrigin } from '../../components/ServiceFrame';
import ReportingSidebar from '../../components/reporting/ReportingSidebar';
import ExecReportPanel from '../../components/reporting/ExecReportPanel';
import FinanceReportPanel from '../../components/reporting/FinanceReportPanel';
import { SafeIframe } from '@saas-foundation/shared-frontend/iframeEmbed';
import { ReportingEmailShell } from '@saas-foundation/shared-frontend/reporting';
import { useOrganizationContext } from '../../agenticsaas/contexts/OrganizationContext';

const Reporting = () => {
  const [searchParams] = useSearchParams();
  const { organizationId } = useOrganizationContext();
  const [activeService, setActiveService] = useState(() => searchParams.get('service') || null);
  const [activeReport, setActiveReport] = useState(() => searchParams.get('report') || null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const service = searchParams.get('service');
    const report = searchParams.get('report');
    if (service) setActiveService(service);
    if (report) setActiveReport(report);
  }, [searchParams]);

  const pushAuthToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const org =
      organizationId ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId');
    let rcm;
    try {
      const raw = sessionStorage.getItem('organization_rcm_settings');
      if (raw) rcm = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    win.postMessage(
      {
        type: 'AUTH_DATA',
        authToken: token || undefined,
        refreshToken: refreshToken || undefined,
        refresh_token: refreshToken || undefined,
        organizationId: org || undefined,
        organizationRcmSettings: rcm || undefined,
      },
      '*'
    );
    if (org) {
      win.postMessage(
        { type: 'ORGANIZATION_ID', organizationId: org, organizationRcmSettings: rcm || undefined },
        '*'
      );
      win.postMessage(
        { type: 'ORGANIZATION_CONTEXT', organizationId: org, organizationRcmSettings: rcm || undefined },
        '*'
      );
    }
  }, [organizationId]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (
        event.data.type === 'REQUEST_AUTH_DATA' ||
        event.data.type === 'REQUEST_ORG_CONTEXT' ||
        event.data.type === 'REQUEST_ORGANIZATION_ID'
      ) {
        pushAuthToIframe();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pushAuthToIframe]);

  // Get service URL for iframe embedding (same auth handoff as ServiceFrame)
  const getServiceReportsUrl = (serviceId, reportId) => {
    const servicePortMap = {
      eligibility: SERVICE_PORTS.INSURANCE_VERIFICATION?.FRONTEND || 3150,
      priorAuth: SERVICE_PORTS.PRIOR_AUTHORIZATION?.FRONTEND || 3151,
      charges: SERVICE_PORTS.CHARGE_CAPTURE?.FRONTEND || 3152,
      claims: SERVICE_PORTS.CLAIMS_PROCESSING?.FRONTEND || 3153,
      arPosting: SERVICE_PORTS.AR_POSTING?.FRONTEND || 3155,
      denial: SERVICE_PORTS.DENIAL_MANAGEMENT?.FRONTEND || 3154,
      remittanceAdvice: SERVICE_PORTS.REMITTANCE_ADVICE?.FRONTEND || 3157,
      ediPlatform: SERVICE_PORTS.EDI_PLATFORM?.FRONTEND || 3010,
      clearingHouse: SERVICE_PORTS.EDI_PLATFORM?.FRONTEND || 3010,
      contractManagement: SERVICE_PORTS.CONTRACT_MANAGEMENT?.FRONTEND || 3156,
      patientBilling: SERVICE_PORTS.PATIENT_ACCOUNT?.FRONTEND || 3110,
      compliance: SERVICE_PORTS.RCM_COMPLIANCE?.FRONTEND || 3158,
    };
    const port = servicePortMap[serviceId] || 3000;

    // Insurance Verification can 404 on deep-link path /reports on some hosts.
    // Use root path there to avoid requiring server-side SPA rewrite rules.
    const serviceBasePathMap = {
      eligibility: '/',
    };
    const basePath = serviceBasePathMap[serviceId] || '/reports';

    // Correct origin per deployment: <slug>.<domain> on real hosts, host:port on localhost/IP.
    const origin = buildServiceOrigin(port);
    const params = new URLSearchParams();
    params.set('embedded', 'true');
    if (reportId) params.set('report', reportId);

    const org =
      organizationId ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId');
    if (org) params.set('organization_id', org);

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) params.set('auth_token', token);
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) params.set('refresh_token', refreshToken);

    // Prefer shell /api (nginx → SaaS) over bare :8001 from the iframe origin.
    try {
      params.set('saas_api_base', window.location.origin);
    } catch {
      /* ignore */
    }

    try {
      const raw = sessionStorage.getItem('organization_rcm_settings');
      if (raw) {
        const rcm = JSON.parse(raw);
        if (rcm && typeof rcm === 'object' && typeof btoa === 'function') {
          params.set('org_rcm', btoa(unescape(encodeURIComponent(JSON.stringify(rcm)))));
        }
      }
    } catch {
      /* ignore */
    }

    return `${origin}${basePath}?${params.toString()}`;
  };

  // Render content based on active service and report
  const renderContent = () => {
    // If no specific service/report selected, show overview
    if (!activeService) {
      return (
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <FileText className="w-8 h-8 mr-3 text-blue-600" />
                  Reporting Center
                </h1>
                <p className="mt-2 text-sm text-gray-600">
                  Access and generate reports from all RCM Platform services
                </p>
              </div>
            </div>

            {/* Service Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Executive Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-indigo-200 p-6 hover:shadow-md transition-shadow ring-1 ring-indigo-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <LineChart className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Executive Reports</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Enterprise scorecards using live RCM KPIs and custom metrics — save snapshots, compare periods, and email from the platform share bar
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Executive KPI snapshot</li>
                  <li>• Department scorecards</li>
                  <li>• Saved reports library</li>
                  <li>• Period-over-period comparison</li>
                  <li>• Email reports to stakeholders</li>
                </ul>
                <button
                  onClick={() => {
                    setActiveService('execReport');
                    setActiveReport('executive-kpi-snapshot');
                  }}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <span>Open Executive Reports</span>
                  <LineChart className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Finance Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-emerald-200 p-6 hover:shadow-md transition-shadow ring-1 ring-emerald-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <Wallet className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Finance Reports</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Peer to Executive Reports — variance vs EHR, capture gaps, aging, and dollars at risk from platform books
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Finance View summary</li>
                  <li>• vs EHR variance</li>
                  <li>• Capture & unmatched lists</li>
                  <li>• Dollars at risk</li>
                </ul>
                <button
                  onClick={() => {
                    setActiveService('financeReport');
                    setActiveReport('finance-view-summary');
                  }}
                  className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center"
                >
                  <span>Open Finance Reports</span>
                  <Wallet className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Denial Management Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Denial Management</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Denial reports, appeal analysis, and recovery metrics
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Denial Summary Report</li>
                  <li>• Appeal Analysis Report</li>
                  <li>• Payer Performance Report</li>
                  <li>• Financial Impact Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('denial')}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Claims Processing Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Claims Processing</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Claims processing reports and performance metrics
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Financial Summary Report</li>
                  <li>• Rejection Analysis Report</li>
                  <li>• Performance Metrics Report</li>
                  <li>• Clean Claim Rate Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('claims')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* AR Posting Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CreditCard className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">AR Posting</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Cash posting, deposit reconciliation, auto-post, and adjustments.
                  Remittance ERA/EOB inventory reports are in Remittance Advice.
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Cash Posting Report</li>
                  <li>• Auto-Post Report</li>
                  <li>• Deposit Reconciliation Report</li>
                  <li>• Adjustment Analysis Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('arPosting')}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Charge Management reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Charge Management</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Charge capture and revenue cycle reports
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Charge Metrics Report</li>
                  <li>• Missing Encounters Report</li>
                  <li>• Charge Lag Report</li>
                  <li>• Revenue Summary Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('charges')}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Prior Authorization Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-yellow-100">
                      <CheckCircle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Prior Authorization</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Authorization reports and approval metrics
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Authorization Summary Report</li>
                  <li>• Provider Performance Report</li>
                  <li>• Patient Demographics Report</li>
                  <li>• Audit Trail Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('priorAuth')}
                  className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Insurance Verification Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <Shield className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Insurance Verification</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Eligibility and benefits verification reports
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Verification Summary Report</li>
                  <li>• Benefits Analysis Report</li>
                  <li>• Patient Responsibility Report</li>
                  <li>• Coverage Status Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('eligibility')}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Contract Management Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-teal-100">
                      <FileText className="w-6 h-6 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Contract Management</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Contract performance and payer agreement reports
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Contract Performance Report</li>
                  <li>• Payer Agreement Analysis</li>
                  <li>• Fee Schedule Comparison</li>
                  <li>• Contract Compliance Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('contractManagement')}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Patient Billing Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-pink-100">
                      <CreditCard className="w-6 h-6 text-pink-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Patient Billing</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Patient billing and payment collection reports
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Billing Summary Report</li>
                  <li>• Payment Collection Report</li>
                  <li>• Outstanding Balance Report</li>
                  <li>• Payment Plan Analysis</li>
                </ul>
                <button
                  onClick={() => setActiveService('patientBilling')}
                  className="w-full px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* RCM Compliance Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <ShieldAlert className="w-6 h-6 text-slate-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">RCM Compliance</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Compliance posture, risk hotspots, and audit workpack extracts
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Executive Compliance Summary</li>
                  <li>• Risk Register & Hotspots</li>
                  <li>• Audit Workpack</li>
                </ul>
                <button
                  onClick={() => {
                    setActiveService('compliance');
                    setActiveReport('executive-compliance');
                  }}
                  className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* Remittance Advice Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-orange-100">
                      <BarChart3 className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Remittance Advice</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  ERA / 835 payment, adjustment, and payer behavior reports
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• RA Volume & Trends Report</li>
                  <li>• Payment Status Report</li>
                  <li>• Financial Summary Report</li>
                  <li>• Payer Drift Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('remittanceAdvice')}
                  className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>

              {/* EDI Clearing House Reports */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gray-100">
                      <Globe className="w-6 h-6 text-gray-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">EDI Platform</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  EDI transaction and connectivity performance reports
                </p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li>• Transaction Log Report</li>
                  <li>• Success Rate Report</li>
                  <li>• Error Analysis Report</li>
                  <li>• Processing Performance Report</li>
                </ul>
                <button
                  onClick={() => setActiveService('ediPlatform')}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                >
                  <span>View Reports</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Native Executive / Finance reports — not iframe-embedded
    if (activeService === 'execReport') {
      return <ExecReportPanel activeReport={activeReport || 'executive-kpi-snapshot'} />;
    }
    if (activeService === 'financeReport') {
      return <FinanceReportPanel activeReport={activeReport || 'finance-view-summary'} />;
    }

    const serviceUrl = getServiceReportsUrl(activeService, activeReport);

    return (
      <SafeIframe
        key={`${activeService}-${activeReport}-${organizationId || 'org'}`}
        src={serviceUrl}
        title={`${activeService} Reports - ${activeReport || 'Overview'}`}
        serviceName={`${activeService} reports`}
        allow="fullscreen"
        style={{ minHeight: '100%', width: '100%' }}
        iframeRef={iframeRef}
        onLoad={pushAuthToIframe}
      />
    );
  };

  const mainContent = renderContent();

  return (
    <div className="flex h-screen overflow-hidden">
      <ReportingSidebar
        activeService={activeService}
        activeReport={activeReport}
        onServiceChange={setActiveService}
        onReportChange={setActiveReport}
      />

      {activeService ? (
        <ReportingEmailShell
          organizationId={organizationId}
          activeService={activeService}
          activeReport={activeReport}
        >
          {mainContent}
        </ReportingEmailShell>
      ) : (
        <div className="flex-1 overflow-hidden">{mainContent}</div>
      )}
    </div>
  );
};

export default Reporting;

